/**
 * Table actions — the interactive half of the sheet. Each action routes the
 * change through the REAL vendored rules engine (health.ts / mana.ts), then
 * writes only the two mutable numbers (healthBarSlotsRemaining, currentMana)
 * back onto the stored Character. The rules engine stays the single authority;
 * this service does no game arithmetic of its own.
 *
 * Actions return a result describing what happened (slots lost, damage ignored,
 * whether a spend was rejected) so the UI can surface it — e.g. DCC's rule that
 * sub-slot damage is ignored, or that mana can't be overspent.
 */
import type { Character } from '@dcc/rules/domain/character.ts';
import { computeSheet } from './characterService.ts';
import { applyDamageToHealthBar, healSlots } from '@dcc/rules/rules/health.ts';
import { spendMana, restoreMana } from '@dcc/rules/rules/mana.ts';

export interface DamageOutcome {
  character: Character;
  slotsLost: number;
  damageIgnored: number;
  reducedToZero: boolean;
}

export interface HealOutcome {
  character: Character;
  slotsHealed: number;
}

export type ManaOutcome =
  | { ok: true; character: Character; }
  | { ok: false; reason: string; };

/** Apply post-mitigation damage. Damage is in Health points; the rules engine
 * converts to whole slots and ignores any sub-slot remainder (DCC p.42). */
export function applyDamage(character: Character, damage: number): DamageOutcome {
  const { derived } = computeSheet(character);
  const result = applyDamageToHealthBar(derived.healthBar, damage);
  return {
    character: { ...character, healthBarSlotsRemaining: result.bar.slotsRemaining },
    slotsLost: result.slotsLost,
    damageIgnored: result.damageIgnored,
    reducedToZero: result.reducedToZero,
  };
}

/** Heal a whole number of slots, clamped to the max by the rules engine. */
export function applyHeal(character: Character, slots: number): HealOutcome {
  const { derived } = computeSheet(character);
  const before = derived.healthBar.slotsRemaining;
  const bar = healSlots(derived.healthBar, slots);
  return {
    character: { ...character, healthBarSlotsRemaining: bar.slotsRemaining },
    slotsHealed: bar.slotsRemaining - before,
  };
}

/** Spend mana; the rules engine rejects an overspend rather than going negative. */
export function applySpendMana(character: Character, cost: number): ManaOutcome {
  const { derived } = computeSheet(character);
  const result = spendMana(derived.mana, cost);
  if (!result.ok) return { ok: false, reason: result.reason };
  return { ok: true, character: { ...character, currentMana: result.pool.current } };
}

/** Restore mana, clamped to the maximum by the rules engine. */
export function applyRestoreMana(character: Character, amount: number): Character {
  const { derived } = computeSheet(character);
  const pool = restoreMana(derived.mana, amount);
  return { ...character, currentMana: pool.current };
}
